import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ProtectedRoute from './lib/ProtectedRoute';
import AdminRoute from './lib/AdminRoute';
import { useAuth } from './lib/AuthContext';
import { isAdmin } from './lib/auth';
import { ImpersonationProvider, useIdentity } from './lib/ImpersonationContext';
import ImpersonationPicker from './lib/ImpersonationPicker';
import Toasts, { toastStore } from './vtt/ui/Toasts';
import { getWorldMapLive } from './lib/worldMapLive';
import { WorldMapServerTypes } from './vtt/net/sceneWire';

// Lazy-load page components so the initial bundle only includes the login
// flow and nav shell. Each page (and its dependencies) is fetched on first
// navigation. Vite's code-splitting handles the chunk boundaries automatically.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Vtt = lazy(() => import('./pages/Vtt'));
const Sheet = lazy(() => import('./pages/Sheet'));
const Party = lazy(() => import('./pages/Party'));
const Lore = lazy(() => import('./pages/Lore'));
const LevelUp = lazy(() => import('./pages/LevelUp'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminMaps = lazy(() => import('./pages/AdminMaps'));
const MapViewer = lazy(() => import('./pages/MapViewer'));
const MapViewerIndex = lazy(() =>
  import('./pages/MapViewer').then((m) => ({ default: m.MapViewerIndex })),
);
const AuditDashboard = lazy(() => import('./pages/AuditDashboard'));

function AppShell() {
  const { user, signOut } = useAuth();
  const { impersonating, effectiveUser, stopImpersonating } = useIdentity();
  const admin = isAdmin(user);
  const navigate = useNavigate();
  // Suppress admin-only nav links while impersonating — the DM should see
  // the nav exactly as the player would. The ImpersonationPicker and the
  // banner's "Return to DM view" button remain visible for control.
  const showAdminNav = admin && !impersonating;

  // ---- Reveal-on-any-page toast (§6.C of the world-map plan). --------
  // While the user is signed in we hold one WebSocket open so a DM reveal
  // surfaces a toast regardless of which page they're on (dashboard, lore,
  // sheet, etc.). Clicking the toast deep-links to the affected map. The
  // MapViewer subscribes to the same singleton to also refresh its marker
  // list when the event is for the map it's rendering.
  useEffect(() => {
    if (!user) return;
    const live = getWorldMapLive();
    const off = live.on(WorldMapServerTypes.MarkerRevealed, (p) => {
      const target = p.mapSlug ?? p.mapId;
      toastStore.push(
        'info',
        `A new location has been revealed: ${p.label}`,
        {
          action: {
            onClick: () => navigate(`/map/${encodeURIComponent(target)}`),
            label: `View ${p.label} on the map`,
          },
          // Reveals deserve a bit more dwell time than a generic toast —
          // if a player is mid-turn we want them to notice without having
          // to dig through the nav for "what just popped up?".
          dismissMs: 10_000,
        },
      );
    });
    void live.start();
    return () => {
      off();
      // Don't stop() on unmount — the singleton should stay connected as
      // long as the user is signed in. We only tear down on sign-out, and
      // even then the effect just drops the listener; a re-sign-in triggers
      // start() again which is idempotent.
    };
  }, [user, navigate]);

  return (
    <div className="app-shell">
      {impersonating && (
        <div className="impersonation-banner">
          <span>
            Viewing as <strong>{effectiveUser?.email || effectiveUser?.username}</strong>
          </span>
          <button onClick={stopImpersonating}>Return to DM view</button>
        </div>
      )}
      <nav className="app-nav">
        <span className="app-nav__brand">GoobDoob VTT</span>
        {user && (
          <>
            <NavLink to="/dashboard" className="app-nav__link">Dashboard</NavLink>
            <NavLink to="/vtt" className="app-nav__link">VTT</NavLink>
            <NavLink to="/sheet" className="app-nav__link">Sheet</NavLink>
            <NavLink to="/party" className="app-nav__link">Party</NavLink>
            <NavLink to="/lore" className="app-nav__link">Lore</NavLink>
            <NavLink to="/levelup" className="app-nav__link">Level Up</NavLink>
            {showAdminNav && <NavLink to="/audit" className="app-nav__link">Audit</NavLink>}
            {showAdminNav && <NavLink to="/admin" className="app-nav__link">Admin</NavLink>}
            {admin && <ImpersonationPicker />}
            <span style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}>
              {user.email || user.username}
            </span>
            <button
              onClick={signOut}
              className="app-nav__link"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Sign out
            </button>
          </>
        )}
      </nav>
      <main className="app-main">
        <Suspense fallback={<p style={{ padding: 'var(--sp-4)', color: 'var(--fg-muted)' }}>Loading…</p>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          <Route
            path="/vtt"
            element={<ProtectedRoute><Vtt /></ProtectedRoute>}
          />
          <Route
            path="/sheet"
            element={<ProtectedRoute><Sheet /></ProtectedRoute>}
          />
          <Route
            path="/party"
            element={<ProtectedRoute><Party /></ProtectedRoute>}
          />
          <Route
            path="/lore"
            element={<ProtectedRoute><Lore /></ProtectedRoute>}
          />
          <Route
            path="/levelup"
            element={<ProtectedRoute><LevelUp /></ProtectedRoute>}
          />
          <Route
            path="/audit"
            element={<AdminRoute><AuditDashboard /></AdminRoute>}
          />
          <Route
            path="/admin"
            element={<AdminRoute><Admin /></AdminRoute>}
          />
          <Route
            path="/admin/maps"
            element={<AdminRoute><AdminMaps /></AdminRoute>}
          />
          {/*
            World-map viewer routes. Step 6 of the world-map plan loosened
            these from AdminRoute to ProtectedRoute: the server-side
            MarkerVisibility filter (see WorldMapVisibility.cs) now
            guarantees players only see markers they're cleared for, so any
            authenticated user can reach the viewer. Map records themselves
            are structural campaign data and are public within the
            campaign.
          */}
          <Route
            path="/map"
            element={<ProtectedRoute><MapViewerIndex /></ProtectedRoute>}
          />
          <Route
            path="/map/:mapId"
            element={<ProtectedRoute><MapViewer /></ProtectedRoute>}
          />
        </Routes>
        </Suspense>
      </main>
      <Toasts />
    </div>
  );
}

export default function App() {
  return (
    <ImpersonationProvider>
      <AppShell />
    </ImpersonationProvider>
  );
}
